<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

// ── Contoh pengeluaran nyata untuk coffee shop ────────────────────────────────
const contohPengeluaran = [
  {
    tanggal: '01 Jul 2026',
    deskripsi: 'Beli biji kopi arabika Gayo 5kg',
    kategori: 'Bahan Baku',
    warna: '#22C55E',
    jumlah: 'Rp 750.000',
    berulang: false,
    struk: true,
  },
  {
    tanggal: '01 Jul 2026',
    deskripsi: 'Tagihan listrik bulan Juni',
    kategori: 'Listrik & Air',
    warna: '#EAB308',
    jumlah: 'Rp 2.150.000',
    berulang: true,
    struk: false,
  },
  {
    tanggal: '02 Jul 2026',
    deskripsi: 'Gaji karyawan part-time Budi',
    kategori: 'Gaji',
    warna: '#8B5CF6',
    jumlah: 'Rp 1.800.000',
    berulang: true,
    struk: false,
  },
  {
    tanggal: '03 Jul 2026',
    deskripsi: 'Beli cup & sedotan 1000 pcs',
    kategori: 'Kemasan',
    warna: '#F97316',
    jumlah: 'Rp 185.000',
    berulang: false,
    struk: true,
  },
  {
    tanggal: '04 Jul 2026',
    deskripsi: 'Servis mesin espresso',
    kategori: 'Perawatan',
    warna: '#EF4444',
    jumlah: 'Rp 350.000',
    berulang: false,
    struk: true,
  },
]

// ── FAQ ────────────────────────────────────────────────────────────────────────
const faq = [
  {
    q: 'Apakah pengeluaran bisa dicatat tanpa foto struk?',
    a: 'Bisa. Foto struk bersifat opsional. Anda tetap bisa simpan catatan pengeluaran tanpa upload foto. Namun untuk pengeluaran besar atau yang membutuhkan bukti (seperti pembelian peralatan), sangat disarankan upload struk sebagai bukti transaksi.',
  },
  {
    q: 'Foto struk apa saja yang bisa diupload?',
    a: 'Format yang didukung: JPG, PNG, dan WEBP. Ukuran file maksimal 2MB. Jika foto terlalu besar, Anda bisa kompres dulu menggunakan aplikasi di smartphone sebelum upload. Di Android bisa pakai "Files by Google", di iPhone cukup kirim via WhatsApp ke diri sendiri (otomatis dikompres).',
  },
  {
    q: 'Bagaimana jika saya salah input jumlah pengeluaran?',
    a: 'Anda bisa edit catatan pengeluaran kapan saja. Buka halaman Pengeluaran, klik ikon pensil di baris yang salah, ubah jumlahnya, dan simpan. Koreksi langsung tercermin di laporan.',
  },
  {
    q: 'Apa itu "Pengeluaran Berulang"?',
    a: 'Pengeluaran berulang adalah biaya yang terjadi setiap bulan dengan jumlah yang sama atau mirip — contohnya tagihan listrik, gaji tetap, sewa tempat. Fitur ini hanya sebagai penanda/label saja. Sistem tidak otomatis menduplikasi catatan setiap bulan — Anda tetap perlu input manual tiap bulan.',
  },
  {
    q: 'Apakah pengeluaran yang sudah disimpan bisa dihapus?',
    a: 'Bisa, selama Anda punya permission expenses.delete. Namun hati-hati — menghapus pengeluaran akan langsung mengubah angka di laporan laba rugi. Jika ragu, jangan hapus. Biarkan saja catatan lama — tidak akan mengganggu operasional.',
  },
  {
    q: 'Bisa tidak filter pengeluaran berdasarkan bulan tertentu?',
    a: 'Bisa. Di halaman Daftar Pengeluaran, gunakan filter "Dari Tanggal" dan "Sampai Tanggal". Contoh: isi Dari Tanggal = 01/06/2026 dan Sampai Tanggal = 30/06/2026 untuk melihat semua pengeluaran bulan Juni.',
  },
  {
    q: 'Apakah pengeluaran termasuk dalam laporan laba rugi?',
    a: 'Ya. Semua pengeluaran yang Anda catat akan masuk sebagai "Biaya Operasional" di Laporan Laba Rugi. Makin lengkap catatan pengeluaran Anda, makin akurat laporan keuntungan bersih toko.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/keuangan" class="text-decoration-none text-medium-emphasis text-body-2">
            Keuangan
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Pengeluaran</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-4 mb-4">
          <VAvatar color="error" variant="tonal" size="64" rounded="lg">
            <VIcon icon="ri-wallet-3-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Pencatatan Pengeluaran</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Catat semua biaya operasional toko agar laporan keuangan Anda akurat
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Panduan lengkap cara mencatat, mengedit, dan menyaring pengeluaran operasional toko.
          Pastikan Anda sudah membuat <RouterLink to="/userguide/keuangan/kategori" class="text-decoration-none font-weight-bold">Kategori Pengeluaran</RouterLink> terlebih dahulu sebelum mulai mencatat.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="error" />
            Mengapa Pengeluaran Harus Dicatat?
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <p class="text-body-2 mb-3">
              Banyak pemilik UMKM yang rajin hitung pemasukan, tapi lupa atau malas mencatat
              pengeluaran. Padahal <strong>keuntungan bersih = pemasukan dikurangi pengeluaran</strong>.
              Jika pengeluaran tidak tercatat, laporan laba rugi jadi tidak akurat — Anda bisa
              mengira untung besar, padahal sebenarnya tipis atau bahkan merugi.
            </p>

            <VAlert type="warning" variant="tonal" class="mb-4">
              <p class="font-weight-bold mb-1">📊 Contoh nyata:</p>
              <p class="text-body-2 mb-1">
                Kopi Senja bulan Juli penjualan <strong>Rp 45.000.000</strong>.
                Kedengarannya besar. Tapi setelah dicatat pengeluaran:
              </p>
              <ul class="text-body-2 mb-1 ps-4">
                <li>Bahan baku: Rp 18.000.000</li>
                <li>Gaji 3 karyawan: Rp 9.000.000</li>
                <li>Sewa tempat: Rp 8.000.000</li>
                <li>Listrik + air: Rp 2.500.000</li>
                <li>Kemasan + lainnya: Rp 1.200.000</li>
              </ul>
              <p class="text-body-2 mb-0 font-weight-bold">
                → Keuntungan bersih = Rp 6.300.000 (bukan Rp 45 juta!)
              </p>
            </VAlert>

            <p class="text-body-2 mb-0">
              Dengan mencatat pengeluaran di PintuKasir, laporan Laba Rugi Anda akan otomatis
              terhitung dengan benar setiap saat.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Contoh tampilan daftar -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-list-check-3" class="me-2" color="primary" />
            Tampilan Daftar Pengeluaran
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <p class="text-body-2 mb-4">
              Begini tampilan halaman Daftar Pengeluaran. Setiap baris adalah satu catatan pengeluaran:
            </p>

            <VTable hover>
              <thead>
                <tr>
                  <th>Tanggal</th>
                  <th>Deskripsi</th>
                  <th>Kategori</th>
                  <th class="text-end">Jumlah</th>
                  <th class="text-center">Berulang</th>
                  <th class="text-center">Struk</th>
                  <th class="text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in contohPengeluaran" :key="item.deskripsi">
                  <td class="text-body-2">{{ item.tanggal }}</td>
                  <td class="text-body-2">{{ item.deskripsi }}</td>
                  <td>
                    <div class="d-flex align-center gap-2">
                      <div :style="`background:${item.warna};width:10px;height:10px;border-radius:50%;flex-shrink:0`" />
                      <span class="text-body-2">{{ item.kategori }}</span>
                    </div>
                  </td>
                  <td class="text-end font-weight-semibold text-error">{{ item.jumlah }}</td>
                  <td class="text-center">
                    <VIcon
                      :icon="item.berulang ? 'ri-repeat-line' : 'ri-subtract-line'"
                      :color="item.berulang ? 'primary' : 'secondary'"
                      size="18"
                    />
                  </td>
                  <td class="text-center">
                    <VIcon
                      :icon="item.struk ? 'ri-image-line' : 'ri-subtract-line'"
                      :color="item.struk ? 'success' : 'secondary'"
                      size="18"
                    />
                  </td>
                  <td class="text-center">
                    <VIcon icon="ri-pencil-line" color="primary" size="18" class="me-1" />
                    <VIcon icon="ri-delete-bin-line" color="error" size="18" />
                  </td>
                </tr>
              </tbody>
            </VTable>

            <VRow class="mt-4">
              <VCol cols="12" sm="6" md="3">
                <div class="d-flex align-center gap-2">
                  <VIcon icon="ri-repeat-line" color="primary" size="18" />
                  <span class="text-body-2">= Pengeluaran berulang bulanan</span>
                </div>
              </VCol>
              <VCol cols="12" sm="6" md="3">
                <div class="d-flex align-center gap-2">
                  <VIcon icon="ri-image-line" color="success" size="18" />
                  <span class="text-body-2">= Ada foto struk</span>
                </div>
              </VCol>
              <VCol cols="12" sm="6" md="3">
                <div class="d-flex align-center gap-2">
                  <VIcon icon="ri-subtract-line" color="secondary" size="18" />
                  <span class="text-body-2">= Tidak ada / tidak berlaku</span>
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara tambah pengeluaran -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Mencatat Pengeluaran Baru
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Pengeluaran</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Tambah Pengeluaran"</strong> di pojok kanan atas
              </li>
              <li class="mb-4">
                Isi form berikut:

                <VTable class="mt-3">
                  <thead>
                    <tr>
                      <th style="min-width:180px">Field</th>
                      <th style="min-width:80px">Wajib?</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Kategori Pengeluaran</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Pilih dari dropdown kategori yang sudah Anda buat.<br>
                        Contoh: pilih <strong>Bahan Baku</strong> untuk pembelian kopi dan susu.<br>
                        <span class="text-caption text-error">
                          Jika tidak ada pilihan yang sesuai, buat kategori baru dulu di menu
                          Pengeluaran → Kategori.
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Deskripsi</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Keterangan singkat tentang pengeluaran ini. Tulis sedetail mungkin
                        agar mudah diingat nanti.<br>
                        Contoh: <strong>"Beli biji kopi arabika Gayo 5kg dari Toko ABC"</strong><br>
                        <span class="text-caption text-medium-emphasis">
                          Hindari deskripsi terlalu umum seperti "pengeluaran" atau "beli barang".
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Jumlah (Rp)</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Total uang yang dikeluarkan dalam Rupiah. Ketik angka saja tanpa titik
                        atau koma — sistem akan format otomatis.<br>
                        Contoh: ketik <strong>750000</strong> untuk Rp 750.000
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Tanggal Pengeluaran</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Tanggal pengeluaran terjadi (bukan tanggal input). Otomatis terisi
                        hari ini, tapi bisa diubah untuk input mundur.<br>
                        <span class="text-caption text-medium-emphasis">
                          Tips: Langsung catat di hari yang sama agar tidak lupa.
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Pengeluaran Berulang</strong></td>
                      <td><VChip color="secondary" size="x-small" variant="tonal">❌ Opsional</VChip></td>
                      <td class="text-body-2 py-2">
                        Aktifkan toggle ini jika pengeluaran ini rutin terjadi setiap bulan
                        (contoh: tagihan listrik, gaji, sewa).<br>
                        Fungsinya sebagai <strong>penanda saja</strong> — sistem tidak
                        otomatis menduplikasi catatan. Anda tetap input manual tiap bulan.
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Foto Struk</strong></td>
                      <td><VChip color="secondary" size="x-small" variant="tonal">❌ Opsional</VChip></td>
                      <td class="text-body-2 py-2">
                        Upload foto struk/kwitansi sebagai bukti pengeluaran.<br>
                        Format: JPG, PNG, WEBP. Maks. <strong>2MB</strong>.<br>
                        <span class="text-caption text-medium-emphasis">
                          Disarankan untuk pengeluaran di atas Rp 100.000 atau yang
                          membutuhkan bukti untuk pembukuan.
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-3">
                Klik <strong>"Simpan Pengeluaran"</strong>
              </li>
              <li>
                Catatan pengeluaran langsung masuk ke daftar dan terfleksi di Laporan Laba Rugi ✅
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Upload foto struk -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-camera-line" class="me-2" color="info" />
            Cara Upload Foto Struk
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <p class="text-body-2 mb-4">
              Upload struk sangat disarankan untuk pengeluaran besar karena bisa jadi
              bukti jika ada pertanyaan dari pihak luar (auditor, mitra, investor).
            </p>

            <VRow>
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-4">
                    <p class="font-weight-bold text-body-2 mb-3">
                      📱 Upload dari HP
                    </p>
                    <ol class="text-body-2 ps-4 mb-0">
                      <li class="mb-2">Di form pengeluaran, klik area <strong>Upload Struk</strong></li>
                      <li class="mb-2">Pilih <strong>"Camera"</strong> untuk foto langsung, atau <strong>"Gallery"</strong> untuk pilih foto yang sudah ada</li>
                      <li class="mb-2">Foto muncul sebagai preview — pastikan struk terbaca jelas</li>
                      <li>Jika ingin ganti foto, klik <strong>"Hapus Foto"</strong> dan upload ulang</li>
                    </ol>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-4">
                    <p class="font-weight-bold text-body-2 mb-3">
                      💻 Upload dari Komputer / Laptop
                    </p>
                    <ol class="text-body-2 ps-4 mb-0">
                      <li class="mb-2">Klik area <strong>Upload Struk</strong></li>
                      <li class="mb-2">Pilih file gambar dari folder di komputer Anda</li>
                      <li class="mb-2">Preview foto muncul di sebelah kanan form</li>
                      <li>Pastikan ukuran file tidak lebih dari <strong>2MB</strong></li>
                    </ol>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <VAlert type="warning" variant="tonal" class="mt-4" density="compact">
              <strong>Tips foto yang baik:</strong> Pastikan struk tidak buram, tidak terlipat,
              dan semua angka terbaca jelas. Foto dalam kondisi cahaya terang. Jika struk
              panjang, foto dari jarak yang cukup jauh agar semua teks terlihat.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Filter dan cari -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-filter-line" class="me-2" color="secondary" />
            Cara Mencari dan Menyaring Daftar Pengeluaran
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <p class="text-body-2 mb-4">
              Di bagian atas halaman Daftar Pengeluaran ada 4 filter yang bisa digunakan
              secara bersamaan:
            </p>

            <VRow>
              <VCol cols="12" md="6">
                <div class="d-flex gap-3 align-start mb-4">
                  <VAvatar color="primary" variant="tonal" size="36" rounded>
                    <VIcon icon="ri-search-line" size="18" />
                  </VAvatar>
                  <div>
                    <p class="font-weight-bold text-body-2 mb-1">Cari Deskripsi</p>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      Ketik kata kunci dari deskripsi pengeluaran.
                      Contoh: ketik "kopi" untuk menemukan semua pembelian kopi.
                    </p>
                  </div>
                </div>
                <div class="d-flex gap-3 align-start">
                  <VAvatar color="error" variant="tonal" size="36" rounded>
                    <VIcon icon="ri-price-tag-3-line" size="18" />
                  </VAvatar>
                  <div>
                    <p class="font-weight-bold text-body-2 mb-1">Filter Kategori</p>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      Tampilkan hanya pengeluaran dari satu kategori tertentu.
                      Contoh: pilih "Bahan Baku" untuk lihat semua pembelian bahan.
                    </p>
                  </div>
                </div>
              </VCol>
              <VCol cols="12" md="6">
                <div class="d-flex gap-3 align-start mb-4">
                  <VAvatar color="success" variant="tonal" size="36" rounded>
                    <VIcon icon="ri-calendar-line" size="18" />
                  </VAvatar>
                  <div>
                    <p class="font-weight-bold text-body-2 mb-1">Filter Dari Tanggal</p>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      Tanggal mulai periode yang ingin dilihat.
                      Contoh: isi 01/07/2026 untuk lihat mulai awal Juli.
                    </p>
                  </div>
                </div>
                <div class="d-flex gap-3 align-start">
                  <VAvatar color="warning" variant="tonal" size="36" rounded>
                    <VIcon icon="ri-calendar-check-line" size="18" />
                  </VAvatar>
                  <div>
                    <p class="font-weight-bold text-body-2 mb-1">Filter Sampai Tanggal</p>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      Tanggal akhir periode. Kombinasikan dengan "Dari Tanggal"
                      untuk melihat pengeluaran dalam rentang bulan tertentu.
                    </p>
                  </div>
                </div>
              </VCol>
            </VRow>

            <VAlert type="info" variant="tonal" class="mt-4" density="compact">
              Untuk mereset semua filter sekaligus, klik ikon
              <VIcon icon="ri-filter-off-line" size="16" /> yang muncul di sebelah filter saat ada filter aktif.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Edit dan hapus -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-edit-line" class="me-2" color="warning" />
            Cara Mengedit Pengeluaran
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">
                Di halaman Daftar Pengeluaran, temukan catatan yang ingin diubah
              </li>
              <li class="mb-2">
                Klik ikon <strong>pensil (✏️)</strong> di kolom Aksi
              </li>
              <li class="mb-2">
                Ubah data yang perlu diperbaiki (kategori, deskripsi, jumlah, tanggal,
                atau struk foto)
              </li>
              <li>
                Klik <strong>"Simpan Perubahan"</strong>
              </li>
            </ol>

            <VAlert type="info" variant="tonal" density="compact">
              <strong>Ganti foto struk:</strong> Di halaman edit, klik tombol
              "Hapus" pada foto lama, lalu upload foto yang baru.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-delete-bin-line" class="me-2" color="error" />
            Cara Menghapus Pengeluaran
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">
                Klik ikon <strong>tempat sampah (🗑️)</strong> di baris yang ingin dihapus
              </li>
              <li class="mb-2">
                Baca dialog konfirmasi — pastikan nama/tanggal yang muncul benar
              </li>
              <li>
                Klik <strong>"Hapus"</strong> untuk konfirmasi
              </li>
            </ol>

            <VAlert type="error" variant="tonal" density="compact">
              <strong>Hati-hati:</strong> Menghapus pengeluaran akan langsung mengubah
              angka di Laporan Laba Rugi. Foto struk yang terlampir juga ikut terhapus permanen.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Peringatan penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips & Best Practices Pencatatan Pengeluaran
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <VRow>
              <VCol cols="12" md="6">
                <VList density="compact" bg-color="transparent">
                  <VListItem prepend-icon="ri-star-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      <strong>Catat pengeluaran di hari yang sama.</strong>
                      Jangan tumpuk catatan di akhir minggu — Anda pasti lupa detail dan
                      nominalnya.
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-star-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      <strong>Foto struk langsung saat terima.</strong>
                      Struk kertas mudah hilang atau pudar. Foto dulu baru simpan fisiknya.
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-star-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      <strong>Pisahkan pengeluaran pribadi dan toko.</strong>
                      Jangan input pengeluaran pribadi (beli makan siang, bensin motor pribadi)
                      ke sistem toko. Ini akan merusak akurasi laporan laba rugi.
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VCol>
              <VCol cols="12" md="6">
                <VList density="compact" bg-color="transparent">
                  <VListItem prepend-icon="ri-star-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      <strong>Review pengeluaran setiap akhir bulan.</strong>
                      Cek apakah ada yang belum dicatat, atau ada yang salah angkanya.
                      Lebih mudah koreksi di akhir bulan daripada 3 bulan kemudian.
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-star-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      <strong>Gunakan deskripsi yang spesifik.</strong>
                      "Beli kopi arabika 5kg dari Toko Jaya" jauh lebih berguna daripada
                      "beli bahan" saat Anda cari data 3 bulan kemudian.
                    </VListItemTitle>
                  </VListItem>
                  <VListItem prepend-icon="ri-star-line">
                    <VListItemTitle class="text-body-2 text-wrap">
                      <strong>Tandai pengeluaran rutin sebagai "Berulang".</strong>
                      Ini memudahkan Anda mengidentifikasi biaya tetap bulanan saat
                      menganalisis laporan keuangan.
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Langkah selanjutnya -->
    <VRow class="mt-6 mb-2">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol v-for="link in [
            {
              title: 'Kategori Pengeluaran',
              sub: 'Buat dan kelola kategori sebelum mencatat pengeluaran',
              icon: 'ri-price-tag-3-line',
              color: 'error',
              to: '/userguide/keuangan/kategori',
            },
            {
              title: 'Laporan Laba Rugi',
              sub: 'Lihat dampak semua pengeluaran ke keuntungan bersih',
              icon: 'ri-bar-chart-line',
              color: 'primary',
              to: '/userguide/laporan/laba-rugi',
            },
            {
              title: 'Dashboard Laporan',
              sub: 'Ringkasan penjualan dan pengeluaran hari ini',
              icon: 'ri-dashboard-line',
              color: 'success',
              to: '/userguide/laporan/dashboard',
            },
          ]" :key="link.title" cols="12" md="4">
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
